import React from 'react';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Textarea from '../components/ui/Textarea';
import { Checkbox, CheckboxGroup } from '../components/ui/Checkbox';
import ProgressBar from '../components/ui/ProgressBar';
import { useBriefForm } from '../hooks/useBriefForm';
import {
  businessTypeOptions,
  yearsInOperationOptions,
  monthlyRevenueOptions,
  staffCountOptions,
  locationOptions,
  geographyOptions,
  roleOptions,
  painPointOptions,
  hoursOptions,
  supplierOptions,
  distributionOptions,
  goalOptions,
  controlAreaOptions,
  commitmentOptions,
  acknowledgements,
} from '../data/formOptions';

function StepBusiness({ formData, errors, updateField }) {
  const showRevenueWarning = formData.monthlyRevenue === 'under-50k';
  const showYearsWarning = formData.yearsInOperation === 'under-1';

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-heading">Business Overview</h2>
        <p className="text-body text-subtle">
          Tell us about your existing operation.
        </p>
      </div>

      <div className="space-y-5 pt-4">
        <Input
          label="Business Name"
          value={formData.businessName}
          onChange={(e) => updateField('businessName', e.target.value)}
          error={errors.businessName}
          required
          placeholder="Your Business Ltd."
        />

        <Select
          label="Business Type"
          value={formData.businessType}
          onChange={(e) => updateField('businessType', e.target.value)}
          options={businessTypeOptions}
          error={errors.businessType}
          required
        />

        <Select
          label="Years in Operation"
          value={formData.yearsInOperation}
          onChange={(e) => updateField('yearsInOperation', e.target.value)}
          options={yearsInOperationOptions}
          error={errors.yearsInOperation}
          required
        />

        {showYearsWarning && (
          <div className="bg-secondary border border-border p-4">
            <p className="text-small text-muted">
              <span className="text-foreground font-medium">Note:</span> We work with
              established businesses with proven demand. Pre-revenue or early-stage
              ventures are typically not a fit.
            </p>
          </div>
        )}

        <Select
          label="Monthly Revenue"
          value={formData.monthlyRevenue}
          onChange={(e) => updateField('monthlyRevenue', e.target.value)}
          options={monthlyRevenueOptions}
          error={errors.monthlyRevenue}
          required
        />

        {showRevenueWarning && (
          <div className="bg-secondary border border-border p-4">
            <p className="text-small text-muted">
              <span className="text-foreground font-medium">Note:</span> Engagements
              require businesses with meaningful revenue. Briefs from operations
              under $50K monthly are typically declined.
            </p>
          </div>
        )}

        <Select
          label="Number of Staff"
          value={formData.staffCount}
          onChange={(e) => updateField('staffCount', e.target.value)}
          options={staffCountOptions}
          error={errors.staffCount}
          required
        />

        <Select
          label="Physical Locations / Production Sites"
          value={formData.locations}
          onChange={(e) => updateField('locations', e.target.value)}
          options={locationOptions}
          error={errors.locations}
          required
        />

        <Select
          label="Primary Geography"
          value={formData.geography}
          onChange={(e) => updateField('geography', e.target.value)}
          options={geographyOptions}
          error={errors.geography}
          required
        />
      </div>
    </div>
  );
}

function StepOperations({ formData, errors, updateField }) {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-heading">Operational Assessment</h2>
        <p className="text-body text-subtle">
          Your current role and operational challenges.
        </p>
      </div>

      <div className="space-y-6 pt-4">
        <Select
          label="Your Role"
          value={formData.role}
          onChange={(e) => updateField('role', e.target.value)}
          options={roleOptions}
          error={errors.role}
          required
        />

        <CheckboxGroup
          label="Primary Operational Pain Points"
          options={painPointOptions}
          values={formData.painPoints}
          onChange={(values) => updateField('painPoints', values)}
          error={errors.painPoints}
          maxSelections={3}
        />

        <Select
          label="Hours You Spend Weekly on Operations"
          value={formData.hoursOnOps}
          onChange={(e) => updateField('hoursOnOps', e.target.value)}
          options={hoursOptions}
          error={errors.hoursOnOps}
          helperText="How much of your time goes to running day-to-day operations?"
          required
        />

        <Select
          label="Number of Suppliers / Vendors"
          value={formData.supplierCount}
          onChange={(e) => updateField('supplierCount', e.target.value)}
          options={supplierOptions}
          error={errors.supplierCount}
          required
        />

        <Select
          label="Current Distribution Reach"
          value={formData.distribution}
          onChange={(e) => updateField('distribution', e.target.value)}
          options={distributionOptions}
          error={errors.distribution}
          required
        />

        <Textarea
          label="Previous Attempts"
          value={formData.previousAttempts}
          onChange={(e) => updateField('previousAttempts', e.target.value)}
          error={errors.previousAttempts}
          maxLength={250}
          required
          placeholder="What have you tried to solve these operational problems? Hiring, software, consultants, etc."
        />
      </div>
    </div>
  );
}

function StepGoalsControl({ formData, errors, updateField }) {
  const showWarning = formData.ownerCommitment === 'maintain-some' ||
                      formData.ownerCommitment === 'unsure';

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-heading">Goals & Control</h2>
        <p className="text-body text-subtle">
          What you want to achieve and what you are willing to surrender.
        </p>
      </div>

      <div className="space-y-6 pt-4">
        <CheckboxGroup
          label="Primary Goals"
          options={goalOptions}
          values={formData.goals}
          onChange={(values) => updateField('goals', values)}
          error={errors.goals}
        />

        <CheckboxGroup
          label="I am willing to fully surrender control of:"
          options={controlAreaOptions}
          values={formData.controlAreas}
          onChange={(values) => updateField('controlAreas', values)}
          error={errors.controlAreas}
        />

        <Select
          label="Owner Commitment"
          value={formData.ownerCommitment}
          onChange={(e) => updateField('ownerCommitment', e.target.value)}
          options={commitmentOptions}
          error={errors.ownerCommitment}
          helperText="After engagement, I am prepared to:"
          required
        />

        {showWarning && (
          <div className="bg-secondary border border-border p-4">
            <p className="text-small text-muted">
              <span className="text-foreground font-medium">Note:</span> Owners who
              wish to maintain operational involvement typically do not qualify.
              Our model requires full operational handover.
            </p>
          </div>
        )}

        <Textarea
          label="Why Bliu Interactive"
          value={formData.whyBliu}
          onChange={(e) => updateField('whyBliu', e.target.value)}
          error={errors.whyBliu}
          maxLength={300}
          required
          placeholder="Why are you submitting this brief? What specifically led you here?"
        />
      </div>
    </div>
  );
}

function StepAcknowledge({ formData, errors, updateField }) {
  const handleAcknowledgement = (index, checked) => {
    if (checked) {
      updateField('acknowledgements', [...formData.acknowledgements, index]);
    } else {
      updateField('acknowledgements', formData.acknowledgements.filter((i) => i !== index));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <h2 className="text-heading">Non-Negotiables</h2>
        <p className="text-body text-subtle">
          Before submitting, you must acknowledge the following:
        </p>
      </div>

      <div className="space-y-4 pt-4">
        {acknowledgements.map((text, index) => (
          <Checkbox
            key={index}
            label={text}
            checked={formData.acknowledgements.includes(index)}
            onChange={(e) => handleAcknowledgement(index, e.target.checked)}
          />
        ))}
        {errors.acknowledgements && (
          <p className="text-caption text-red-400">{errors.acknowledgements}</p>
        )}
      </div>

      <div className="space-y-5 pt-4">
        <Input
          label="Primary Contact Email"
          type="email"
          value={formData.email}
          onChange={(e) => updateField('email', e.target.value)}
          error={errors.email}
          required
          placeholder="you@business.com"
        />

        <Input
          label="Phone Number"
          type="tel"
          value={formData.phone}
          onChange={(e) => updateField('phone', e.target.value)}
          placeholder="+1 (555) 000-0000"
          helperText="Optional, but helpful for initial contact"
        />
      </div>
    </div>
  );
}

function Confirmation() {
  return (
    <div className="space-y-8 animate-fade-in text-center py-12">
      <div className="space-y-4">
        <h2 className="text-heading">Brief received.</h2>
        <div className="text-body text-subtle space-y-4 max-w-md mx-auto">
          <p>
            Your submission will be reviewed within 10 business days.
            If your brief is accepted, you will receive terms at the email provided.
          </p>
          <p>
            Most briefs are declined. No response indicates a decline.
          </p>
          <p className="text-muted">
            Do not follow up.
          </p>
        </div>
      </div>

      <Button to="/" variant="secondary">
        Return to Home
      </Button>
    </div>
  );
}

export function Brief() {
  const {
    step,
    totalSteps,
    formData,
    errors,
    isSubmitting,
    isSubmitted,
    updateField,
    nextStep,
    prevStep,
    submit,
  } = useBriefForm();

  if (isSubmitted) {
    return (
      <Layout hideFooter>
        <section className="section">
          <div className="container-narrow">
            <Confirmation />
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout hideFooter>
      {/* Header */}
      <section className="py-8 md:py-12 border-b border-border">
        <div className="container-narrow">
          <div className="space-y-4">
            <h1 className="text-display-sm">Submit a Brief</h1>
            <p className="text-body text-subtle max-w-lg">
              This is not a contact form. This is an operational assessment for
              existing, revenue-generating businesses. Most briefs are declined.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section">
        <div className="container-narrow">
          <div className="space-y-10">
            <ProgressBar currentStep={step} totalSteps={totalSteps} />

            {step === 1 && (
              <StepBusiness
                formData={formData}
                errors={errors}
                updateField={updateField}
              />
            )}
            {step === 2 && (
              <StepOperations
                formData={formData}
                errors={errors}
                updateField={updateField}
              />
            )}
            {step === 3 && (
              <StepGoalsControl
                formData={formData}
                errors={errors}
                updateField={updateField}
              />
            )}
            {step === 4 && (
              <StepAcknowledge
                formData={formData}
                errors={errors}
                updateField={updateField}
              />
            )}

            {errors.submit && (
              <p className="text-small text-red-400">{errors.submit}</p>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 border-t border-border">
              {step > 1 ? (
                <Button variant="ghost" onClick={prevStep}>
                  ← Back
                </Button>
              ) : (
                <div />
              )}

              {step < totalSteps ? (
                <Button onClick={nextStep}>
                  Continue →
                </Button>
              ) : (
                <Button onClick={submit} disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Brief'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Brief;
